import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavigationHeader from "../../components/ui/NavigationHeader";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import ActiveCampaignsCard from "../accounts-receivable-management-dashboard/components/ActiveCampaignsCard";
import { authenticatedFetch } from "../../auth/auth";

function apiBase() {
  return (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || "").replace(/\/$/, "");
}

function formatDateTime(value) {
  if (!value) return "Sem registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).replace("T", " ").slice(0, 16);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function MetricCard({ icon, label, value, helper, accent = "default" }) {
  const accentClasses = {
    default: "border-border bg-card",
    primary: "border-primary/20 bg-primary/5",
    success: "border-emerald-500/20 bg-emerald-500/5",
    warning: "border-amber-500/20 bg-amber-500/5",
  };

  return (
    <div className={`rounded-2xl border p-4 card-shadow ${accentClasses[accent] || accentClasses.default}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
          <div className="mt-2 text-2xl font-heading-semibold text-card-foreground">{value}</div>
          <div className="mt-2 text-sm text-muted-foreground">{helper}</div>
        </div>

        <div className="w-11 h-11 rounded-xl border border-border bg-background flex items-center justify-center shrink-0">
          <Icon name={icon} size={18} />
        </div>
      </div>
    </div>
  );
}

function ChannelStatusCard({ title, description, icon, statusLabel, statusTone = "neutral", details = [] }) {
  const toneClasses = {
    neutral: "bg-background text-muted-foreground border-border",
    positive: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
    warning: "bg-amber-500/10 text-amber-700 border-amber-500/20",
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 card-shadow h-full">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl border border-border bg-background flex items-center justify-center shrink-0">
            <Icon name={icon} size={18} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-heading-semibold text-card-foreground">{title}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
        </div>

        <span className={`px-3 py-1 rounded-full text-xs font-body-medium border whitespace-nowrap ${toneClasses[statusTone] || toneClasses.neutral}`}>
          {statusLabel}
        </span>
      </div>

      <div className="mt-4 space-y-2">
        {details.map((detail) => (
          <div key={detail.label} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">{detail.label}</span>
            <span className="text-card-foreground font-body-medium text-right">{detail.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionShell({ title, subtitle, icon, children, actions = null }) {
  return (
    <section className="rounded-3xl border border-border bg-card p-6 card-shadow">
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl border border-border bg-background flex items-center justify-center shrink-0">
            <Icon name={icon} size={20} />
          </div>
          <div>
            <h2 className="text-lg font-heading-semibold text-card-foreground">{title}</h2>
            <p className="mt-1 text-sm text-muted-foreground max-w-3xl">{subtitle}</p>
          </div>
        </div>

        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>

      {children}
    </section>
  );
}

const BillingAutomationDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);

  async function loadSummary() {
    setLoading(true);
    setError("");

    try {
      const [cfgRes, histRes] = await Promise.all([
        authenticatedFetch(`${apiBase()}/api/email/config`, { cache: "no-store" }),
        authenticatedFetch(`${apiBase()}/api/email/history?limit=100&channel=all`, { cache: "no-store" }),
      ]);

      const cfg = await cfgRes.json();
      const hist = await histRes.json();

      if (!cfg?.ok) throw new Error(cfg?.message || "Falha ao carregar automação.");
      if (!hist?.ok) throw new Error(hist?.message || "Falha ao carregar histórico.");

      setConfig(cfg);
      setHistory(Array.isArray(hist.items) ? hist.items : []);
    } catch (e) {
      setError(e?.message || "Falha ao carregar visão geral da automação.");
      setConfig(null);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadSummary();
  }, []);

  const summary = useMemo(() => {
    const emailRules = Array.isArray(config?.rules) ? config.rules : [];
    const chatcenterRules = Array.isArray(config?.chatcenterRules) ? config.chatcenterRules : [];

    const activeEmailRules = emailRules.filter((item) => item?.enabled).length;
    const activeChatcenterRules = chatcenterRules.filter((item) => item?.enabled).length;
    const allRules = [...emailRules, ...chatcenterRules];

    const todayKey = new Date().toISOString().slice(0, 10);
    const sentToday = history.filter((item) => String(item?.sentAt || item?.createdAt || "").slice(0, 10) === todayKey && item?.status === "sent");
    const failedToday = history.filter((item) => String(item?.sentAt || item?.createdAt || "").slice(0, 10) === todayKey && item?.status !== "sent");

    const lastSent = history
      .filter((item) => item?.status === "sent")
      .sort((a, b) => String(b?.sentAt || b?.createdAt || "").localeCompare(String(a?.sentAt || a?.createdAt || "")))[0];

    return {
      activeEmailRules,
      activeChatcenterRules,
      activeCampaigns: activeEmailRules + activeChatcenterRules,
      totalRules: allRules.length,
      sentToday: sentToday.length,
      failedToday: failedToday.length,
      lastSentAt: lastSent?.sentAt || lastSent?.createdAt || "",
      totalHistory: history.length,
    };
  }, [config, history]);

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />

      <main className="max-w-7xl mx-auto px-4 pb-8 pt-24 space-y-6">
        <section className="relative overflow-hidden rounded-3xl border border-border bg-card card-shadow">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 pointer-events-none" />

          <div className="relative p-6 lg:p-8">
            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-6">
              <div className="max-w-3xl">

                <h1 className="text-2xl lg:text-3xl font-heading-semibold text-card-foreground">
                  Cobranças Automáticas
                </h1>
                <p className="mt-3 text-sm lg:text-base text-muted-foreground leading-6 max-w-2xl">
                  Uma área mais limpa para acompanhar campanhas, ajustar mensagens e revisar o histórico sem misturar a automação com a operação diária do contas a receber.
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="px-3 py-1 rounded-full text-xs border border-border bg-background text-card-foreground">E-mail</span>
                  <span className="px-3 py-1 rounded-full text-xs border border-border bg-background text-card-foreground">WhatsApp via ChatCenter</span>
                  <span className="px-3 py-1 rounded-full text-xs border border-border bg-background text-card-foreground">Templates</span>
                  <span className="px-3 py-1 rounded-full text-xs border border-border bg-background text-card-foreground">Histórico e testes</span>
                </div>
              </div>

              <div className="xl:min-w-[280px] flex flex-col gap-3">
                <div className="rounded-2xl border border-border bg-background/80 p-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Última execução com envio</div>
                  <div className="mt-2 text-base font-heading-semibold text-card-foreground">
                    {loading ? "Carregando..." : formatDateTime(summary.lastSentAt)}
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">
                    {loading ? "Consultando histórico recente." : `${summary.sentToday} envio(s) bem-sucedido(s) hoje.`}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="default" size="sm" onClick={loadSummary} disabled={loading}>
                    <Icon name="RefreshCw" size={16} className={loading ? "animate-spin" : ""} />
                    <span className="ml-2">Atualizar visão</span>
                  </Button>

                  <Button variant="outline" size="sm" onClick={() => navigate("/accounts-receivable-management-dashboard")}>
                    <Icon name="ArrowLeft" size={16} />
                    <span className="ml-2">Voltar</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {error ? (
          <div className="rounded-2xl border border-border bg-accent/30 p-4 text-sm text-muted-foreground">{error}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard
              icon="Workflow"
              label="Campanhas ativas"
              value={loading ? "..." : String(summary.activeCampaigns)}
              helper={loading ? "Carregando regras atuais." : `${summary.totalRules} regra(s) configurada(s) na central.`}
              accent="primary"
            />
            <MetricCard
              icon="Send"
              label="Envios hoje"
              value={loading ? "..." : String(summary.sentToday)}
              helper="Mensagens entregues por e-mail e WhatsApp no dia atual."
              accent="success"
            />
            <MetricCard
              icon="AlertTriangle"
              label="Falhas hoje"
              value={loading ? "..." : String(summary.failedToday)}
              helper="Ajuda a revisar rapidamente erros antes de liberar para toda a base."
              accent="warning"
            />
            <MetricCard
              icon="History"
              label="Histórico recente"
              value={loading ? "..." : String(summary.totalHistory)}
              helper="Quantidade de registros carregados para análise rápida do canal."
            />
          </div>
        )}

        <SectionShell
          title="Visão operacional"
          subtitle="Um resumo visual dos canais ativos e do que vale revisar antes de abrir as campanhas abaixo."
          icon="LayoutDashboard"
        >
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <ChannelStatusCard
              title="E-mail"
              description="Canal ideal para mensagem formal com HTML, link do boleto e tentativa de anexar PDF."
              icon="Mail"
              statusLabel={loading ? "Carregando" : summary.activeEmailRules > 0 ? "Canal configurado" : "Sem regra ativa"}
              statusTone={summary.activeEmailRules > 0 ? "positive" : "warning"}
              details={[
                { label: "Regras ativas", value: loading ? "..." : `${summary.activeEmailRules}` },
                { label: "Uso ideal", value: "Cobrança formal e histórico visual" },
                { label: "Ação", value: "Editar assunto, HTML e frequência" },
              ]}
            />

            <ChannelStatusCard
              title="WhatsApp via ChatCenter"
              description="Canal rápido para lembretes diretos com texto simples e link do boleto."
              icon="MessageCircle"
              statusLabel={loading ? "Carregando" : summary.activeChatcenterRules > 0 ? "Canal configurado" : "Sem regra ativa"}
              statusTone={summary.activeChatcenterRules > 0 ? "positive" : "warning"}
              details={[
                { label: "Regras ativas", value: loading ? "..." : `${summary.activeChatcenterRules}` },
                { label: "Uso ideal", value: "Cobrança curta e leitura rápida" },
                { label: "Ação", value: "Editar texto, título e repetição" },
              ]}
            />

            <ChannelStatusCard
              title="Organização da central"
              description="A página foi separada para o cliente visualizar melhor a automação sem poluir o financeiro."
              icon="Layers3"
              statusLabel="Fluxo mais limpo"
              statusTone="neutral"
              details={[
                { label: "Templates", value: "Editados sem sair da tela" },
                { label: "Histórico", value: "Acesso por modal com filtros" },
                { label: "Objetivo", value: "Operar mais rápido e com menos erro" },
              ]}
            />
          </div>
        </SectionShell>

        <SectionShell
          title="Campanhas, templates e histórico"
          subtitle="Abaixo ficam as configurações principais da régua. Eu mantive a funcionalidade e melhorei só a apresentação para ficar mais profissional e mais fácil de bater o olho."
          icon="Settings2"
        >
          <ActiveCampaignsCard />
        </SectionShell>
      </main>
    </div>
  );
};

export default BillingAutomationDashboard;
